import type { OrganizationDepartmentRecordList } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  NavioOverlayActions,
  NoddiButton,
  NoddiDialog,
  useAppForm,
  usePagination
} from 'noddi-ui-web';
import { getUserDisplayName } from 'noddi-util';
import { z } from 'zod';
import { buildUserSelectOption, renderUserSelectOption } from '../UserGroups/userSelectOption';

const FORM_ID = 'change-department-primary-contact-form';
const schema = z.object({
  member: z.object({ value: z.number(), label: z.string() }).optional()
});

const defaultValues: z.input<typeof schema> = { member: undefined };

type Props = {
  department: OrganizationDepartmentRecordList | null;
  onClose: () => void;
};

export const ChangeDepartmentPrimaryContactDialog = ({ department, onClose }: Props) =>
  department ? <PrimaryContactForm key={department.id} department={department} onClose={onClose} /> : null;

const PrimaryContactForm = ({
  department,
  onClose
}: {
  department: OrganizationDepartmentRecordList;
  onClose: () => void;
}) => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const {
    data: members,
    isFetching: isMembersFetching,
    error: membersError,
    refetch
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedDepartmentMembers,
    input: { departmentId: department.id, pageIndex, pageSize, search },
    queryConfig: { placeholderData: keepPreviousData }
  });
  const { mutateAsync: patchMember, error: patchError } = noddiAsync.usePatch({
    type: URLKeys.patchDepartmentMember,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId: department.userGroupId })
    }
  });
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      if (!value.member) {
        return;
      }
      try {
        await patchMember({ userGroupMemberId: value.member.value, isPrimaryContact: true }, { onSuccess: onClose });
      } catch {
        // Keep the dialog open with the backend refusal below the picker.
      }
    }
  });
  const currentContact = department.primaryContact ? getUserDisplayName(department.primaryContact) : null;

  return (
    <form.AppForm>
      <NoddiDialog
        open
        onClose={onClose}
        title='Change primary contact'
        description={`${currentContact ? `${currentContact} is the current primary contact.` : 'This customer has no primary contact.'} Departments that share this customer share one primary contact, so this change applies to all of them.`}
        footer={
          <NavioOverlayActions>
            <NoddiButton type='button' variant='outline' onClick={onClose}>
              Cancel
            </NoddiButton>
            <form.Subscribe selector={(state) => state.values.member}>
              {(member) => (
                <form.SubscribeButton
                  formId={FORM_ID}
                  label='Save'
                  disabledReason={!member ? 'Choose a new primary contact' : undefined}
                />
              )}
            </form.Subscribe>
          </NavioOverlayActions>
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          {membersError && (
            <div role='alert'>
              <ApiErrorMessageInternal error={membersError} />
              <NoddiButton variant='outline' onClick={() => void refetch()}>
                Retry
              </NoddiButton>
            </div>
          )}
          <form.AppField name='member'>
            {(field) => (
              <field.AsyncSelect
                label='New primary contact'
                placeholder='Choose a member'
                searchPlaceholder='Search members'
                emptyMessage='No other members'
                options={(members?.results ?? [])
                  .filter((member) => !member.isPrimaryContact)
                  .map((member) => ({ ...buildUserSelectOption(member.user), value: member.id }))}
                isLoading={isMembersFetching}
                clearable={false}
                onSearch={setSearch}
                renderOption={renderUserSelectOption}
              />
            )}
          </form.AppField>
          {patchError && (
            <div role='alert'>
              <ApiErrorMessageInternal error={patchError} />
            </div>
          )}
        </form>
      </NoddiDialog>
    </form.AppForm>
  );
};
